import React, { useEffect, useMemo, useRef, useState } from "react";
import Header from "./components/layouts/Header";
import SchedulerTimeline from "./SchedulerTimeline";
import { DEFAULT_CALENDAR_OTHERS } from "@/components/modals/GlobalCalendarModal.tsx";
import SchedulerEquity from "./SchedulerEquity";
import ConstraintSetting from "./ConstraintSetting";
import type { DateFilter, Definition, DepartmentConfigs, Schedule, Shift } from "./types"; // fusion import
import { ErrorNotificationContainer } from "./components/notification/ErrorNotification";
import { useErrorNotification } from "./hooks/useErrorNotification";
import { useBlockBackNavigation } from "./hooks/useBlockBackNavigation";
import ScoreAnalysisModal from "./components/modals/ScoreAnalysisModal";
import { type ScoreAnalysis } from "./components/modals/ScoreAnalysisModal.types";
import { getScoreComponents } from "./utils/score";
import SchedulerToolbar from "./components/toolbar/SchedulerToolbar";
import SolverStatusBar from "./components/sections/SolverStatusBar";
import type { SolverGainDelta } from "./components/sections/SolverStatusBar.types";
import {
    analyzeSchedule,
    applyChanges,
    benchmark,
    deleteSavedSchedule,
    fetchConstraints,
    fetchDefinitions,
    fetchExtendedWgData,
    fetchSavedSchedule,
    fetchSchedule,
    fetchSolverConfigs,
    fetchWeightOverrides,
    getMutationVersion,
    getScheduleData,
    saveSchedule,
    startSolving,
    stopSolving as apiStopSolving
} from "./api";
import type { ScheduleLoadState, SchedulerAppContext } from "@/SchedulerApp.types.ts";
import FullScreenSpinner from "./components/sections/FullScreenSpinner";
import ScheduleUnavailableNotice from "./components/sections/ScheduleUnavailableNotice";
import { classifyFetchError } from "./utils/fetchError";
import RolesSettings from "./tabs/SchedulerRoles/SchedulerRoles";
import RoleCombinations from "./tabs/RoleCombinations/RoleCombinations";
import type { CombinationRole } from "./tabs/RoleCombinations/RoleCombinations.types";
import { IS_DEBUG } from "@/global.ts";
import { ChevronLeft } from "lucide-react";
import { format, max, min, parseISO } from "date-fns";
import SaveModal from "./components/modals/SaveModal";
import SolverPhasesSelectionModal from "@/components/modals/SolverPhasesSelectionModal.tsx";
import ShiftActionsModal from "@/components/modals/ShiftActionsModal.tsx";
import SolvingLockModal from "./components/modals/SolvingLockModal";
import ShiftNotVisibleModal, { type ShiftNotVisibleReason } from "./components/modals/ShiftNotVisibleModal";
import { isShiftVisibleForFilter } from "./utils/schedulerUtils";

const isShiftWithinFilter = (shift: Shift, filter: DateFilter): boolean => {
    const day = format(parseISO(shift.start), "yyyy-MM-dd");
    return day >= filter.startDate && day <= filter.endDate;
};

const scopedShifts = (
    shifts: Shift[],
    period: DateFilter | null,
): { scope: Shift[]; assigned: number; total: number } => {
    const phaseShifts = shifts.filter(
        (shift) => shift.phaseAllowed !== false && (!period || isShiftWithinFilter(shift, period))
    );

    const scope = phaseShifts.length ? phaseShifts : shifts;

    return { scope, assigned: scope.filter((shift) => shift.employee).length, total: scope.length };
};

// Generation period standard: it starts at local midnight and ends 1ms before the next one,
// so both bounds stay on the day the user picked whatever the timezone offset is.
const periodStartEpoch = (dateStr: string): number => new Date(dateStr + "T00:00:00").getTime();

const periodEndEpoch = (dateStr: string): number => {
    const nextMidnight = new Date(dateStr + "T00:00:00");
    nextMidnight.setDate(nextMidnight.getDate() + 1);

    return nextMidnight.getTime() - 1;
};

// Extracted so the alerts can be recomputed when the constraints change, without paying for a
// new score analysis: the opt-out only filters what is already in the last analysis.
const computeShiftAlerts = (
    analysisConstraints: any[],
    scheduleConstraints: Schedule["constraints"] | undefined,
): { problematicIds: string[]; shiftBreaches: { [shiftId: string]: string[] } } => {
    const problematicIds: string[] = [];
    const shiftBreaches: { [shiftId: string]: string[] } = {};
    const constraintDisplayNames: { [constraintName: string]: string } = {};
    const silencedConstraintIds = new Set<string>();

    if (scheduleConstraints) {
        for (const c of scheduleConstraints) {
            if (c.name && c.displayName) {
                constraintDisplayNames[c.name] = c.displayName;
            }

            if (c.hideShiftAlerts === true && c.id) {
                silencedConstraintIds.add(c.id);
            }
        }
    }

    for (const constraint of analysisConstraints) {
        if (constraint.matches && Array.isArray(constraint.matches)) {
            for (const match of constraint.matches) {
                const matchScore = getScoreComponents(String(match.score));

                if (matchScore.hard < 0 || matchScore.medium < 0 || matchScore.soft < 0) {
                    if (match.justification) {
                        let justification = match.justification
                        if (!justification.shift)
                            continue
                        // Only a purely soft breach may be silenced: `priority_type`
                        // does not always match what the solver scores (an `undesired`
                        // set to soft still penalizes hard), and a hard or medium
                        // breach must stay visible whatever the constraint says.
                        const silenced = matchScore.hard === 0
                            && matchScore.medium === 0
                            && matchScore.soft < 0
                            && !!justification.constraintId
                            && silencedConstraintIds.has(justification.constraintId);

                        if (!silenced && !problematicIds.includes(justification.shift?.id)) {
                            problematicIds.push(justification.shift.id);
                        }

                        const displayName = justification.name
                        const breachTextPart1 = `${displayName} : ${justification.score}`;
                        const breachTextPart2 = justification.note || "";
                        const breachText = [breachTextPart1, breachTextPart2].filter((a) => !!a).join(" | ");
                        // const breachText = breachTextPart1;
                        const shiftBreachList = (shiftBreaches[justification.shift?.id] ??= []);

                        if (!shiftBreachList.includes(breachText)) {
                            shiftBreachList.push(breachText);
                        }
                    }
                }
            }
        }
    }


    return { problematicIds, shiftBreaches };
};

export default function SchedulerApp() {

    const { errors, showError, removeError, showSimpleError } = useErrorNotification();
    useBlockBackNavigation();

    const tabs = [
        { id: 1, label: "Prévisualisation par rôle" },
        { id: 2, label: "Prévisualisation par employé" },
        { id: 3, label: "Équité" },
        { id: 5, label: "Besoins" },
        { id: 4, label: "Contraintes" },
        //{ id: 6, label: "Combinaisons" },
    ];

    const SHOW_MOCK_PLANIFICO_BAR = false;
    const REFRESH_INTERVAL = 10000;
    const REFRESH_INTERVAL_FAST = 3000;
    const FAST_POLL_WINDOW = 20000;
    const HEADER_COLLAPSED_KEY = "planifico:headerCollapsed";

    const DATE_FILTER_KEY = (() => {
        const p = new URLSearchParams(window.location.search);
        return `planifico:dateFilter:${p.get("bizId")}_${p.get("wgId")}`;
    })();

    const [activeTab, setActiveTab] = useState<number>(tabs[0]?.id ?? 1);
    const [headerCollapsed, setHeaderCollapsed] = useState<boolean>(() => {
        try {
            return localStorage.getItem(HEADER_COLLAPSED_KEY) === "1";
        } catch {
            return false;
        }
    });
    const [loadedSchedule, setLoadedSchedule] = useState<Schedule | null>(null);
    const loadedScheduleRef = useRef<Schedule | null>(loadedSchedule);
    loadedScheduleRef.current = loadedSchedule;
    const [scheduleLoadState, setScheduleLoadState] = useState<ScheduleLoadState>("loading");
    const scheduleLoadStateRef = useRef<ScheduleLoadState>("loading");
    const [depConfigs, setSolverConfigs] = useState<DepartmentConfigs>({});

    const [scheduleId, setScheduleId] = useState<string | null>(null);
    const [currentPhaseId, setCurrentPhaseId] = useState<string | undefined>();
    const [solving, setSolving] = useState<boolean>(false);
    const [startedSolving, setStartedSolving] = useState<boolean>(false);
    const autoRefreshIntervalId = useRef<ReturnType<typeof setInterval> | null>(null);
    const wasSolvingRef = useRef<boolean>(false);
    const didAutoAnalyzeForScheduleRef = useRef<string | null>(null);

    const [startDate, setStartDate] = useState<string | null>(null);
    const [endDate, setEndDate] = useState<string | null>(null);
    const [dateFilter, setDateFilter] = useState<DateFilter | null>(() => {
        try {
            const raw = localStorage.getItem(DATE_FILTER_KEY);
            if (!raw) {
                return null;
            }

            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed.startDate === "string" && typeof parsed.endDate === "string") {
                return parsed as DateFilter;
            }

            return null;
        } catch {
            return null;
        }
    });
    const [analysisOpen, setAnalysisOpen] = useState(false);
    const [analysisData, setAnalysisData] = useState<ScoreAnalysis | null>(null);
    const analysisCacheRef = useRef<{ key: string; data: ScoreAnalysis } | null>(null);
    const analysisEpochRef = useRef<number>(0);

    const invalidateAnalysis = () => {
        analysisCacheRef.current = null;
        analysisEpochRef.current++;
    };
    const scheduleFetchVersionRef = useRef<number>(0);
    const [definitions, setDefinitions] = useState<Definition[] | null>(null);

    const [unassignedShiftsCount, setUnassignedShiftCount] = useState<number | null>(null);
    const [initialUnassignedShiftsCount, setInitialUnassignedShiftsCount] = useState<number | null>(null);

    const [gainDelta, setGainDelta] = useState<SolverGainDelta | null>(null);
    const [liveScore, setLiveScore] = useState<{ hard: number; soft: number } | null>(null);
    const [solveTotal, setSolveTotal] = useState<number>(0);
    const [solveAssigned, setSolveAssigned] = useState<number>(0);
    const solveScopeIdsRef = useRef<Set<string> | null>(null);
    const solveBaselineRef = useRef<{ assignedStart: number; minHard: number; minSoft: number } | null>(null);
    const [problematicShiftIds, setProblematicShiftIds] = useState<string[]>([]);
    const [shiftConstraintBreaches, setShiftConstraintBreaches] = useState<{ [shiftId: string]: string[] }>({});

    // Toggling "hide alerts" on a constraint must move the outlines right away. The analysis itself
    // is unchanged by that flag, so the cached one is re-filtered instead of being requested again.
    useEffect(() => {
        const cachedAnalysis = analysisCacheRef.current?.data as { constraints?: any[] } | undefined;

        if (!cachedAnalysis?.constraints) {
            return;
        }

        const { problematicIds } = computeShiftAlerts(
            cachedAnalysis.constraints,
            loadedSchedule?.constraints,
        );

        // Re-emitting an identical array would rebuild the whole vis DataSet on every constraint
        // save, losing the timeline position. shiftBreaches is never filtered, so it never changes.
        setProblematicShiftIds((prev) => (
            prev.length === problematicIds.length && prev.every((id, i) => id === problematicIds[i])
                ? prev
                : problematicIds
        ));
    }, [loadedSchedule?.constraints]);
    const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
    const [isApplying, setIsApplying] = useState<boolean>(false);
    const [isAnalyzing, setIsAnalyzing] = useState<boolean>(false);
    const [isStopping, setIsStopping] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [saveModalOpen, setSaveModalOpen] = useState(false);
    const [savedListRefreshKey, setSavedListRefreshKey] = useState(0);
    const [selectedRoles, setSelectedRoles] = useState<Set<string>>(new Set());
    const [selectedEmployees, setSelectedEmployees] = useState<Set<string>>(new Set());
    const [selectedCalendarOthers, setSelectedCalendarOthers] = useState<Set<string>>(
        () => new Set(DEFAULT_CALENDAR_OTHERS)
    );

    const [confirmOpenPhaseSelect, setConfirmOpenPhaseSelect] = useState(false);
    const [confirmOpenShiftActions, setConfirmOpenShiftActions] = useState(false);
    const [solvingLockOpen, setSolvingLockOpen] = useState(false);
    const [shiftNotVisibleReason, setShiftNotVisibleReason] = useState<ShiftNotVisibleReason | null>(null);
    const [pendingFocus, setPendingFocus] = useState<{ shift: Shift; seq: number } | null>(null);
    const focusSeqRef = useRef<number>(0);
    const handledFocusSeqRef = useRef<number>(0);
    const focusHighlightRef = useRef<{ shiftId: string; pulseTimeoutId: ReturnType<typeof setTimeout> } | null>(null);
    const focusRetryRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const focusScrollRef = useRef<{ stop: () => void; timeoutId: ReturnType<typeof setTimeout> } | null>(null);
    const shiftActionsOpenRef = useRef(confirmOpenShiftActions);
    shiftActionsOpenRef.current = confirmOpenShiftActions;
    const solvingRef = useRef(solving);
    solvingRef.current = solving;

    const [selectedShiftId, setSelectedShiftId] = useState<string>();

    const isInteractingRef = useRef<boolean>(false);
    const pendingRefreshRef = useRef<boolean>(false);
    const suppressTimelineRebuildRef = useRef<boolean>(false);
    const [appContext, setAppContext] = useState<SchedulerAppContext>({ isInteractingRef, suppressTimelineRebuildRef });

    const applyUrlDates = () => {
        const url_params = new URLSearchParams(window.location.search)
        let startEpoch: number = Number.parseInt(url_params.get("fromEpoch") || "")

        if (Number.isNaN(startEpoch)) {
            startEpoch = (new Date()).getTime()
        }

        // Read back in local time, mirroring periodStartEpoch/periodEndEpoch: both bounds
        // already sit inside the day the user picked, so no shifting is needed here.
        setStartDate(format(new Date(startEpoch), "yyyy-MM-dd"))

        const endEpoch: number = Number.parseInt(url_params.get("toEpoch") || "")

        if (Number.isNaN(endEpoch)) {
            setEndDate(null)
        } else {
            setEndDate(format(new Date(endEpoch), "yyyy-MM-dd"))
        }
    };

    const applySavedScheduleDates = (schedule: Schedule) => {
        const meta = schedule.scheduleMetadata;
        const startISO = meta?.minDate ?? schedule.startDate;
        const start = startISO ? parseISO(startISO) : null;
        const end = meta?.maxDate ? parseISO(meta.maxDate) : null;
        const isValid = (d: Date | null): d is Date => d != null && !Number.isNaN(d.getTime());

        if (isValid(start) && isValid(end)) {
            setStartDate(format(start, "yyyy-MM-dd"));
            setEndDate(format(end, "yyyy-MM-dd"));
            return;
        }

        applyScheduleDates(schedule);
    };

    const savedSchedulePeriodEpochs = (schedule: Schedule): { fromEpoch: number; toEpoch: number } | null => {
        const meta = schedule.scheduleMetadata;
        const startISO = meta?.minDate ?? schedule.startDate;
        const start = startISO ? parseISO(startISO) : null;
        const end = meta?.maxDate ? parseISO(meta.maxDate) : null;
        const isValid = (d: Date | null): d is Date => d != null && !Number.isNaN(d.getTime());

        if (isValid(start) && isValid(end)) {
            return {
                fromEpoch: periodStartEpoch(format(start, "yyyy-MM-dd")),
                toEpoch: periodEndEpoch(format(end, "yyyy-MM-dd")),
            };
        }

        const starts = (schedule.shifts ?? []).map((s) => parseISO(s.start)).filter(isValid);
        const ends = (schedule.shifts ?? []).map((s) => parseISO(s.end)).filter(isValid);

        if (starts.length === 0 || ends.length === 0) {
            return null;
        }

        const maxEnd = max(ends);
        if (maxEnd.getHours() === 0 && maxEnd.getMinutes() === 0 && maxEnd.getSeconds() === 0) {
            maxEnd.setDate(maxEnd.getDate() - 1);
        }

        return {
            fromEpoch: periodStartEpoch(format(min(starts), "yyyy-MM-dd")),
            toEpoch: periodEndEpoch(format(maxEnd, "yyyy-MM-dd")),
        };
    };

    const syncUrlPeriod = (fromEpoch: number, toEpoch: number) => {
        const url_params = new URLSearchParams(window.location.search);
        url_params.set("fromEpoch", fromEpoch.toString());
        url_params.set("toEpoch", toEpoch.toString());
        window.history.replaceState(null, "", `${window.location.pathname}?${url_params.toString()}${window.location.hash}`);
    };

    const applyScheduleDates = (schedule: Schedule) => {
        if (!schedule.shifts || schedule.shifts.length === 0) {
            return;
        }

        const isValidDate = (d: Date) => !Number.isNaN(d.getTime());
        const starts = schedule.shifts.map(s => parseISO(s.start)).filter(isValidDate);
        const ends = schedule.shifts.map(s => parseISO(s.end)).filter(isValidDate);

        if (starts.length === 0 || ends.length === 0) {
            return;
        }

        setStartDate(format(min(starts), "yyyy-MM-dd"));

        const maxEnd = max(ends);

        if (maxEnd.getHours() === 0 && maxEnd.getMinutes() === 0 && maxEnd.getSeconds() === 0) {
            maxEnd.setDate(maxEnd.getDate() - 1);
        }
        setEndDate(format(maxEnd, "yyyy-MM-dd"));
    };

    useEffect(() => {
        refreshSchedule(null);
        fetchDefinitions().then((res) => setDefinitions(res))
        updateUIDepConfigs();
        applyUrlDates();

        return () => {
            if (autoRefreshIntervalId.current != null) {
                clearTimeout(autoRefreshIntervalId.current);
            }
        };
    }, []);

    useEffect(() => {
        if (loadedSchedule) {
            setUnassignedShiftCount(loadedSchedule.shifts.filter(shift => !shift.employee).length)
        }
    }, [loadedSchedule])

    useEffect(() => {
        if (!solving) {
            solveBaselineRef.current = null;
            solveScopeIdsRef.current = null;
        }
    }, [solving])

    const clearSolveIndicators = () => {
        setGainDelta(null);
        setLiveScore(null);
    };

    useEffect(() => {
        if (solving) {
            // Explain why the shift actions modal is being closed under the user.
            if (shiftActionsOpenRef.current) {
                setSolvingLockOpen(true);
            }
            setConfirmOpenShiftActions(false);
        } else {
            setSolvingLockOpen(false);
        }
    }, [solving])

    useEffect(() => {
        try {
            localStorage.setItem(HEADER_COLLAPSED_KEY, headerCollapsed ? "1" : "0");
        } catch {
            /* localStorage indisponible (mode privé) — ignore */
        }
    }, [headerCollapsed])

    useEffect(() => {
        try {
            if (dateFilter) {
                localStorage.setItem(DATE_FILTER_KEY, JSON.stringify(dateFilter));
            } else {
                localStorage.removeItem(DATE_FILTER_KEY);
            }
        } catch {
            /* localStorage indisponible (mode privé) — ignore */
        }
    }, [dateFilter])

    /**
     * Filtres
     */
    const knownRolesRef = useRef<Set<string>>(new Set());
    const knownEmployeesRef = useRef<Set<string>>(new Set());

    useEffect(() => {
        if (!loadedSchedule) {
            return;
        }

        const allRoles = new Set<string>();

        for (const shift of loadedSchedule.shifts) {
            const role = shift.roleName || shift.requiredSkill || 'Autre';
            allRoles.add(role);
        }

        for (const employee of loadedSchedule.employees) {
            const roles = employee.roles?.map(r => r?.name).filter(Boolean) || [];
            roles.forEach(role => allRoles.add(role));
        }

        const allEmployees = new Set(loadedSchedule.employees.map(emp => emp.userId));

        const newRoles = [...allRoles].filter(role => !knownRolesRef.current.has(role));
        const newEmployees = [...allEmployees].filter(id => !knownEmployeesRef.current.has(id));

        if (newRoles.length > 0) {
            newRoles.forEach(role => knownRolesRef.current.add(role));
            setSelectedRoles(prev => new Set([...prev, ...newRoles]));
        }

        if (newEmployees.length > 0) {
            newEmployees.forEach(id => knownEmployeesRef.current.add(id));
            setSelectedEmployees(prev => new Set([...prev, ...newEmployees]));
        }
    }, [loadedSchedule])


    useEffect(() => {
        if (scheduleId) {
            refreshSolvingButtons(true)
        }
    }, [scheduleId])

    const scheduleIdRef = useRef<string | null>(null);
    scheduleIdRef.current = scheduleId;

    const updateUIDepConfigs = async () => {
        let solverConfigs = await fetchSolverConfigs();
        setSolverConfigs(solverConfigs || {})
    }
    //should be deprecated
    const updateConstraints = async () => {
        try {
            let constraints = await fetchConstraints();
            let weightsOverrides = await fetchWeightOverrides();
            // let solverConfigs = await fetchSolverConfigs();

            setSolverConfigs(depConfigs || {})
            if (constraints && weightsOverrides) {
                setLoadedSchedule((prev) => ({
                    ...prev,
                    ...({
                        constraints: constraints,
                        constraintWeightOverrides: weightsOverrides,
                        //   solverConfigs:solverConfigs
                    } as Partial<Schedule> as Schedule)
                }))
            }
        } catch (e) {
            console.log(e)
        }
    }

    const setLoadState = (next: ScheduleLoadState) => {
        scheduleLoadStateRef.current = next;
        setScheduleLoadState(next);
    };

    const handleFetchFailure = (error: unknown, message: string) => {
        const kind = classifyFetchError(error);
        const willRenderNotice = scheduleLoadStateRef.current !== "ready";

        if (kind !== "noData" || !willRenderNotice) {
            showError(message, error);
        }

        if (!willRenderNotice) {
            return;
        }

        setLoadState(kind === "noData" ? "empty" : "error");
    };

    const refreshSchedule = (interval: any) => {
        const currentScheduleId = scheduleIdRef.current;
        fetchSchedule(currentScheduleId)
            .then((schedule: Schedule) => {
                if (currentScheduleId !== scheduleIdRef.current) {
                    return;
                }

                setLoadState("ready");
                scheduleFetchVersionRef.current++;

                const isSolving = schedule.solverStatus != null && schedule.solverStatus !== "NOT_SOLVING";
                const assignedCount = schedule.shifts.filter((s: any) => s.employee).length;


                if (!interval || !isSolving)
                    setLoadedSchedule(schedule);
                else {
                    setUnassignedShiftCount(schedule.shifts.filter(shift => !shift.employee).length);

                    const scopeIds = solveScopeIdsRef.current;
                    if (scopeIds) {
                        const freshAssigned = schedule.shifts
                            .filter((shift) => scopeIds.has(shift.id) && shift.employee).length;
                        const scoreComponents = getScoreComponents(schedule.score);

                        if (solveBaselineRef.current === null) {
                            solveBaselineRef.current = {
                                assignedStart: freshAssigned,
                                minHard: scoreComponents.hard,
                                minSoft: scoreComponents.soft
                            };
                        } else {
                            const baseline = solveBaselineRef.current;

                            if (scoreComponents.hard < baseline.minHard) {
                                baseline.minHard = scoreComponents.hard;
                            }

                            if (scoreComponents.soft < baseline.minSoft) {
                                baseline.minSoft = scoreComponents.soft;
                            }
                        }
                        const baseline = solveBaselineRef.current;
                        setGainDelta({
                            quarts: freshAssigned - baseline.assignedStart,
                            soft: Math.round(scoreComponents.soft - baseline.minSoft),
                            hard: Math.round(scoreComponents.hard - baseline.minHard),
                        });
                        setLiveScore({ hard: scoreComponents.hard, soft: scoreComponents.soft });
                        setSolveAssigned(freshAssigned);
                    }
                }

                refreshSolvingButtons(isSolving);

                if (!isSolving && wasSolvingRef.current && scheduleId &&
                    didAutoAnalyzeForScheduleRef.current !== scheduleId && schedule.score != null) {
                    didAutoAnalyzeForScheduleRef.current = scheduleId;
                    wasSolvingRef.current = false;
                    analyze(schedule);
                    setSavedListRefreshKey((k) => k + 1);
                } else {
                    setLoadedSchedule(old => {
                        let temp = old
                        temp.shifts = schedule.shifts
                        return { ...temp }
                    });
                }

                // //fetchExtendedWgData().catch((error) => {
                //     //console.error("Error calling fetchExtendedWgData:", error);
                // //});
            })
            .catch((error) => {
                if (currentScheduleId !== scheduleIdRef.current) {
                    return;
                }
                refreshSolvingButtons(false);

                if (IS_DEBUG) {
                    showError("Getting the schedule has failed.", error);
                    getScheduleData().then((schedule) => {
                        // schedule.
                        setLoadedSchedule(schedule);
                        setLoadState("ready");
                        fetchExtendedWgData().catch((error) => {
                            console.error("Error calling fetchExtendedWgData:", error);
                        });
                    });
                    return;
                }

                handleFetchFailure(error, "Getting the schedule has failed.");
            });
    };

    const refreshSolvingButtons = (isSolving: boolean) => {
        setSolving(isSolving);
        if (isSolving) {
            setStartedSolving(true);
            if (autoRefreshIntervalId.current == null) {
                const solveStart = Date.now();
                const tick = () => {
                    if (scheduleIdRef.current) {
                        if (isInteractingRef.current) {
                            pendingRefreshRef.current = true;
                        } else {
                            pendingRefreshRef.current = false;
                            refreshSchedule(true);
                        }
                    }
                    const delay = (Date.now() - solveStart) < FAST_POLL_WINDOW
                        ? REFRESH_INTERVAL_FAST
                        : REFRESH_INTERVAL;
                    autoRefreshIntervalId.current = globalThis.setTimeout(tick, delay);
                };
                autoRefreshIntervalId.current = globalThis.setTimeout(tick, 300);
            }
            wasSolvingRef.current = true;
        } else {
            if (autoRefreshIntervalId.current != null) {
                clearTimeout(autoRefreshIntervalId.current);
                autoRefreshIntervalId.current = null;
            }
        }
    };

    const internalSolve = (schedule: Schedule, phaseId?: string, applyDateFilter?: boolean) => {
        const activeFilter = applyDateFilter && dateFilter ? dateFilter : null;

        var selectedPhase = phaseId ? (depConfigs.mapOfPhases || {})[phaseId] : void 0;
        //@ Patch schedule to phase allowed flag
        if (selectedPhase && selectedPhase.active !== false) {
            setCurrentPhaseId(phaseId);
            const mapOfNeedSettings: typeof selectedPhase.mapOfNeedSettings = selectedPhase.mapOfNeedSettings || {};
            schedule.shifts.forEach((shift) => {
                let isPhaseAllowed = false;
                var roleNeed = shift.roleId && mapOfNeedSettings[shift.roleId];
                if (shift.start && roleNeed && roleNeed.apply === true) {
                    const mapOfDayOptions = roleNeed.mapOfDayOptions || {};
                    const dayOfWeek = (new Date(shift.start)).getDay();
                    var dayOptionForShift = mapOfDayOptions[dayOfWeek];
                    if (dayOptionForShift && dayOptionForShift.apply === true) {
                        isPhaseAllowed = true;
                    }
                }

                // Date filter
                shift.phaseAllowed = isPhaseAllowed && (!activeFilter || isShiftWithinFilter(shift, activeFilter));
            });
        } else {
            setCurrentPhaseId(void 0);
            schedule.shifts.forEach((shift) => {
                // Date filter
                shift.phaseAllowed = !activeFilter || isShiftWithinFilter(shift, activeFilter);
            });
        }

        setSolving(true);
        didAutoAnalyzeForScheduleRef.current = null;
        setInitialUnassignedShiftsCount(unassignedShiftsCount);
        setProblematicShiftIds([]);
        setShiftConstraintBreaches({});
        setAnalysisData(null);
        invalidateAnalysis();

        const period = startDate && endDate ? { startDate, endDate } : null;
        const { scope: scopeShifts, assigned: baseAssigned } = scopedShifts(schedule.shifts, period);

        solveScopeIdsRef.current = new Set(scopeShifts.map((shift) => shift.id));
        setSolveTotal(scopeShifts.length);
        setSolveAssigned(baseAssigned);
        solveBaselineRef.current = null;
        setGainDelta(null);
        setLiveScore(null);

        startSolving(schedule, phaseId)
            .then((id) => {
                setScheduleId(id);
            })
            .catch((error) => {
                showError("Start solving failed.", error);
                refreshSolvingButtons(false);
            });

    };
    const preserveTimelineScroll = () => {
        const tld = appContext.locationTimeline;
        const anchor = tld?.anchorNode as HTMLElement | undefined;
        const sp = anchor?.querySelector('.vis-panel.vis-vertical-scroll') as HTMLElement | null;

        if (!sp || sp.scrollTop <= 0) {
            return;
        }

        const savedTop = sp.scrollTop;
        const timeline = tld?.timeline;

        const restore = () => {
            if (sp.scrollTop !== savedTop) {
                sp.scrollTop = savedTop;
            }
        };

        restore();

        if (!timeline) {
            return;
        }

        timeline.on('changed', restore);
        setTimeout(() => timeline.off('changed', restore), 1000);
    };

    const FOCUS_HIGHLIGHT_STYLE = "outline:3px solid #2563eb;outline-offset:2px;";
    const FOCUS_HIGHLIGHT_CLASS = "shift-focus-pulse";
    const FOCUS_PULSE_MS = 2400;

    const stopFocusScroll = () => {
        const scroll = focusScrollRef.current;
        if (!scroll) {
            return;
        }

        clearTimeout(scroll.timeoutId);
        scroll.stop();
        focusScrollRef.current = null;
    };

    const clearFocusHighlight = () => {
        const previous = focusHighlightRef.current;
        if (!previous) {
            return;
        }

        clearTimeout(previous.pulseTimeoutId);
        focusHighlightRef.current = null;

        const timelineData = appContext.locationTimeline;
        const items = timelineData?.itemsDataSet;
        const current = items?.get(previous.shiftId) as { style?: string; className?: string } | null;

        if (items && current?.style?.includes(FOCUS_HIGHLIGHT_STYLE)) {
            items.update([{
                id: previous.shiftId,
                style: current.style.replace(FOCUS_HIGHLIGHT_STYLE, ""),
                className: (current.className ?? "").replace(FOCUS_HIGHLIGHT_CLASS, "").trim(),
            }]);
        }

        const timeline = timelineData?.timeline;
        const selection = timeline?.getSelection();
        if (timeline && selection?.length === 1 && String(selection[0]) === previous.shiftId) {
            timeline.setSelection([]);
        }
    };

    const highlightFocusedShift = (shiftId: string) => {
        clearFocusHighlight();

        const items = appContext.locationTimeline?.itemsDataSet;
        const item = items?.get(shiftId) as { style?: string; className?: string } | null;

        if (!items || !item) {
            return;
        }

        const baseStyle = item.style ?? "";
        const separator = !baseStyle || baseStyle.trimEnd().endsWith(";") ? "" : ";";
        const baseClassName = item.className ?? "";

        items.update([{
            id: shiftId,
            style: `${baseStyle}${separator}${FOCUS_HIGHLIGHT_STYLE}`,
            className: `${baseClassName} ${FOCUS_HIGHLIGHT_CLASS}`.trim(),
        }]);

        const dropPulse = () => {
            const still = items.get(shiftId) as { className?: string } | null;
            if (still?.className?.includes(FOCUS_HIGHLIGHT_CLASS)) {
                items.update([{ id: shiftId, className: still.className.replace(FOCUS_HIGHLIGHT_CLASS, "").trim() }]);
            }
        };

        focusHighlightRef.current = { shiftId, pulseTimeoutId: setTimeout(dropPulse, FOCUS_PULSE_MS) };
    };

    const scrollFocusedShiftIntoView = (groupId: string) => {
        const timelineData = appContext.locationTimeline;
        const anchor = timelineData?.anchorNode as HTMLElement | undefined;
        const scrollPanel = anchor?.querySelector('.vis-panel.vis-vertical-scroll') as HTMLElement | null;
        const labels = anchor?.querySelectorAll('.vis-labelset .vis-label');
        const groupIndex = (timelineData?.groupsDataSet?.getIds({ order: 'order' }) ?? []).indexOf(groupId);
        const label = groupIndex >= 0 ? labels?.[groupIndex] as HTMLElement | undefined : undefined;

        if (!scrollPanel || !label) {
            return;
        }

        const labelBox = label.getBoundingClientRect();
        const panelBox = scrollPanel.getBoundingClientRect();
        const delta = (labelBox.top + labelBox.height / 2) - (panelBox.top + panelBox.height / 2);

        scrollPanel.scrollTop += delta;
    };

    const MAX_FOCUS_ATTEMPTS = 6;

    const applyShiftFocus = (shift: Shift, seq: number, attempt: number = 0) => {
        if (attempt === 0 && focusRetryRef.current != null) {
            clearTimeout(focusRetryRef.current);
            focusRetryRef.current = null;
        }

        if (seq !== focusSeqRef.current || (activeTab !== 1 && activeTab !== 2)) {
            return;
        }

        const timelineData = appContext.locationTimeline;
        const timeline = timelineData?.timeline;
        const hasItem = !!timelineData?.itemsDataSet?.get(shift.id);

        if (!timeline || !hasItem) {
            if (attempt < MAX_FOCUS_ATTEMPTS) {
                focusRetryRef.current = setTimeout(() => applyShiftFocus(shift, seq, attempt + 1), 120);
                return;
            }

            setShiftNotVisibleReason({ kind: "notInSchedule" });
            return;
        }

        const current = timeline.getWindow();
        const spanMs = +current.end - +current.start;
        const shiftCenter = (new Date(shift.start).getTime() + new Date(shift.end).getTime()) / 2;

        if (!Number.isFinite(shiftCenter)) {
            setShiftNotVisibleReason({ kind: "notInSchedule" });
            return;
        }

        const start = new Date(shiftCenter - spanMs / 2);
        const end = new Date(shiftCenter + spanMs / 2);

        timeline.setWindow(start, end, { animation: false });

        highlightFocusedShift(shift.id);
        timeline.setSelection([shift.id]);

        const groupId = activeTab === 2
            ? (shift.employee?.userId ?? "")
            : (shift.roleName || shift.requiredSkill || "Autre");

        stopFocusScroll();
        const scrollToGroup = () => scrollFocusedShiftIntoView(groupId);
        scrollToGroup();
        timeline.on('changed', scrollToGroup);
        focusScrollRef.current = {
            stop: () => timeline.off('changed', scrollToGroup),
            timeoutId: setTimeout(stopFocusScroll, 1500),
        };
    };

    useEffect(() => {
        if (!pendingFocus || handledFocusSeqRef.current === pendingFocus.seq) {
            return;
        }

        handledFocusSeqRef.current = pendingFocus.seq;
        applyShiftFocus(pendingFocus.shift, pendingFocus.seq);
    }, [pendingFocus, activeTab]);

    useEffect(() => () => {
        clearFocusHighlight();
        stopFocusScroll();
        if (focusRetryRef.current != null) {
            clearTimeout(focusRetryRef.current);
        }
    }, []);

    const focusShiftInTimeline = (shift: Shift) => {
        if (activeTab !== 1 && activeTab !== 2) {
            setShiftNotVisibleReason({ kind: "noTimeline" });
            return;
        }

        const targetTab = activeTab === 2 && !shift.employee ? 1 : activeTab;
        const mode = targetTab === 1 ? "byLocation" : "byEmployee";

        if (!isShiftVisibleForFilter(shift, mode, selectedRoles, selectedEmployees)) {
            setShiftNotVisibleReason({
                kind: "filtered",
                mode,
                label: mode === "byEmployee"
                    ? (shift.employee?.name ?? "")
                    : (shift.roleName || shift.requiredSkill || "Autre"),
            });
            return;
        }

        setAnalysisOpen(false);

        if (targetTab !== activeTab) {
            setActiveTab(targetTab);
        }

        focusSeqRef.current += 1;
        setPendingFocus({ shift, seq: focusSeqRef.current });
    };

    const solve = () => {
        if (!loadedSchedule || solving) {
            return;
        }

        var phasesEntries = Object.entries(depConfigs.mapOfPhases || {}).filter((entry) => {
            if (entry[1].active !== false) {
                return true;
            }
            return false;
        });

        preserveTimelineScroll();

        if (phasesEntries.length) {
            setConfirmOpenPhaseSelect(true);
        } else {
            internalSolve(loadedSchedule)
        }

    };

    const stopSolving = () => {
        if (!scheduleId || isStopping) {
            return;
        }

        setIsStopping(true);
        //setScheduleId(null);
        apiStopSolving(scheduleId, currentPhaseId)
            .then(() => {
                refreshSolvingButtons(false);
                setStartedSolving(true);
                refreshSchedule(null);
            })
            .catch((error) => {
                showError("Stop solving failed.", error);
            })
            .finally(() => {
                setIsStopping(false);
            });
    };

    const buildAnalysisKey = (schedule: Schedule): string => {
        const shiftsSignature = (schedule.shifts ?? [])
            .map((shift) => `${shift.id}:${shift.employee?.userId ?? ""}:${shift.pinned ? 1 : 0}:${shift.phaseAllowed === false ? 0 : 1}`)
            .join("|");

        return [
            currentPhaseId ?? "",
            getMutationVersion(),
            scheduleFetchVersionRef.current,
            JSON.stringify(schedule.constraints ?? null),
            JSON.stringify(schedule.constraintWeightOverrides ?? null),
            shiftsSignature,
        ].join("§");
    };

    const analyze = (current_schedule?: Schedule) => {
        const schedule = current_schedule || loadedSchedule;

        if (!schedule || isAnalyzing) {
            return;
        }

        const hasAssignments = schedule.shifts?.some((s) => s.employee) ?? false;
        if (!hasAssignments) {
            setAnalysisData(null);
            setAnalysisOpen(true);
            return;
        }

        const analysisKey = buildAnalysisKey(schedule);
        const cached = analysisCacheRef.current;

        if (cached && cached.key === analysisKey) {
            setAnalysisData(cached.data);
            setAnalysisOpen(true);
            return;
        }

        const analysisEpoch = analysisEpochRef.current;

        setIsAnalyzing(true);
        analyzeSchedule(schedule, currentPhaseId)
            .then(({ analysis: scoreAnalysis, combinationsDegraded }) => {
                if (analysisEpoch !== analysisEpochRef.current) {
                    return;
                }

                const recomputedScore = scoreAnalysis?.score;
                if (recomputedScore != null) {
                    setLoadedSchedule((prev) => (prev ? { ...prev, score: recomputedScore } : prev));
                }

                let constraints = scoreAnalysis.constraints as any[];
                constraints.sort((a, b) => {
                    const aC = getScoreComponents(String(a.score));
                    const bC = getScoreComponents(String(b.score));

                    if (aC.hard < 0 && bC.hard > 0) {
                        return -1;
                    }

                    if (aC.hard > 0 && bC.soft < 0) {
                        return 1;
                    }

                    if (Math.abs(aC.hard) > Math.abs(bC.hard)) {
                        return -1;
                    }

                    if (aC.medium < 0 && bC.medium > 0) {
                        return -1;
                    }

                    if (aC.medium > 0 && bC.medium < 0) {
                        return 1;
                    }

                    if (Math.abs(aC.medium) > Math.abs(bC.medium)) {
                        return -1;
                    }

                    const aSoft = Math.abs(aC.soft);
                    const bSoft = Math.abs(bC.soft);

                    return bSoft - aSoft;
                });

                constraints = constraints.map((c) => {
                    const w = getScoreComponents(String(c.weight));
                    let type: "hard" | "medium" | "soft" = "soft";

                    if (w.hard !== 0) {
                        type = "hard";
                    } else if (w.medium !== 0) {
                        type = "medium";
                    }

                    const s = getScoreComponents(String(c.score));
                    let implicit = s.soft;

                    if (s.hard !== 0) {
                        implicit = s.hard;
                    } else if (s.medium !== 0) {
                        implicit = s.medium;
                    }

                    return { ...c, type, weight: String(c.weight), implicitScore: implicit };
                });

                // loadedSchedule rather than `schedule`: the auto-analysis after a solve is fed the
                // polling payload, whose constraints predate any flag toggled in the meantime.
                const { problematicIds, shiftBreaches } = computeShiftAlerts(
                    constraints,
                    loadedScheduleRef.current?.constraints ?? schedule.constraints,
                );

                setProblematicShiftIds(problematicIds);
                setShiftConstraintBreaches(shiftBreaches);

                const analysis = { constraints };

                if (combinationsDegraded) {
                    showSimpleError("Combinaisons de rôles indisponibles — les bris affichés peuvent être erronés.");
                } else {
                    analysisCacheRef.current = { key: analysisKey, data: analysis };
                }

                setAnalysisData(analysis);
                setAnalysisOpen(true);
            })
            .catch((error) => {
                showError("Analyze failed.", error);
            })
            .finally(() => {
                setIsAnalyzing(false);
            });
    };

    const apply = () => {
        if (isApplying) {
            return;
        }

        setIsApplying(true);
        applyChanges(loadedSchedule)
            .then(() => {
                window.parent.postMessage("close", "*");
                console.log("done")
            })
            .catch((error) => {
                showError("error sending changes", error);
            })
            .finally(() => {
                setIsApplying(false);
            });
    };

    const handleDatesChange = (startDateStr: string, endDateStr: string) => {
        const fromEpoch = periodStartEpoch(startDateStr);
        const toEpoch = periodEndEpoch(endDateStr);

        const url_params = new URLSearchParams(window.location.search);
        url_params.set("fromEpoch", fromEpoch.toString());
        url_params.set("toEpoch", toEpoch.toString());

        // replace() instead of assigning location.search: reloading with a new period must not
        // stack a history entry, or going back would restore the previous generation period.
        window.location.replace(`${window.location.pathname}?${url_params.toString()}${window.location.hash}`);
    };

    const handleRefresh = () => {
        if (isRefreshing) {
            return;
        }

        setIsRefreshing(true);

        setScheduleId(null);
        setSolving(false);
        setStartedSolving(false);
        setAnalysisData(null);
        invalidateAnalysis();
        setProblematicShiftIds([]);
        setShiftConstraintBreaches({});
        setInitialUnassignedShiftsCount(null);
        setGainDelta(null);
        setLiveScore(null);
        solveBaselineRef.current = null;
        solveScopeIdsRef.current = null;
        didAutoAnalyzeForScheduleRef.current = null;
        wasSolvingRef.current = false;

        if (autoRefreshIntervalId.current != null) {
            clearTimeout(autoRefreshIntervalId.current);
            autoRefreshIntervalId.current = null;
        }

        Promise.all([
            fetchSchedule(null),
            fetchDefinitions(),
            updateUIDepConfigs()
        ])
            .then(([schedule, definitions]) => {
                scheduleFetchVersionRef.current++;
                setLoadedSchedule(schedule);
                setLoadState("ready");
                setDefinitions(definitions);
                applyUrlDates();
                setIsRefreshing(false);
            })
            .catch((error) => {
                handleFetchFailure(error, "Refresh failed.");
                setIsRefreshing(false);
            });
    };

    const fetchBenchmark = () => {
        benchmark(loadedSchedule)
    }

    const onSaveSchedule = () => {
        setSaveModalOpen(true);
    }

    const handleSaveWithName = (name: string) => {
        if (!loadedSchedule || isSaving) {
            return;
        }

        setIsSaving(true);

        saveSchedule(loadedSchedule, name)
            .then((res) => {
                setSaveModalOpen(false);
                setSavedListRefreshKey((k) => k + 1);
                if (res?.id) {
                    onImportSchedule(res.id).catch(() => {
                    });
                }
            })
            .catch((error) => showError("Sauvegarde échouée.", error))
            .finally(() => setIsSaving(false));
    }

    const onImportSchedule = async (id: string) => {
        setIsRefreshing(true);
        try {
            invalidateAnalysis();
            let schedule = await fetchSavedSchedule(id)
            const saved = schedule as unknown as Schedule;
            scheduleFetchVersionRef.current++;
            applySavedScheduleDates(saved)
            clearSolveIndicators();

            if (appContext.locationTimeline) {
                appContext.locationTimeline.appliedStartDate = undefined;
            }
            setAppContext((prev) => ({
                ...prev, timelineWindow: undefined
            }))

            let livePeriodSchedule: Schedule | null = null;
            let liveFetchFailed = false;
            const periodEpochs = savedSchedulePeriodEpochs(saved);
            if (periodEpochs) {
                syncUrlPeriod(periodEpochs.fromEpoch, periodEpochs.toEpoch);
                try {
                    livePeriodSchedule = await fetchSchedule(null);
                } catch (error) {
                    liveFetchFailed = true;
                    showError("Congés/indispos live indisponibles pour la période de la sauvegarde — données de la sauvegarde utilisées (possiblement périmées).", error);
                }
            }
            setLoadedSchedule((current) => ({
                ...(schedule as unknown as Schedule),
                constraints: current?.constraints,
                constraintWeightOverrides: current?.constraintWeightOverrides,
            }))

            setLoadedSchedule((current) => {
                const preferLive = <T,>(live: T | undefined, cur: T | undefined, savedVal: T | undefined): T | undefined =>
                    live ?? (liveFetchFailed ? savedVal ?? cur : cur ?? savedVal);

                const employees = preferLive(livePeriodSchedule?.employees, current?.employees, saved.employees);

                const liveEmployeeById = new Map(
                    (employees ?? []).map((e) => [e.userId, e] as const)
                );

                const shifts = saved.shifts.map((s) =>
                    s.employee
                        ? { ...s, employee: liveEmployeeById.get(s.employee.userId) ?? s.employee }
                        : s
                );
                return {
                    ...saved,
                    shifts,
                    constraints: current?.constraints,
                    constraintWeightOverrides: current?.constraintWeightOverrides,
                    employees: employees ?? saved.employees,
                    holidays: preferLive(livePeriodSchedule?.holidays, current?.holidays, saved.holidays),
                    roles: preferLive(livePeriodSchedule?.roles, current?.roles, saved.roles),
                    score: null, // clear the score so we don't show a stale value
                };
            })
        } catch (error) {
            showError("Chargement de la sauvegarde échoué.", error)
            throw error
        } finally {
            setIsRefreshing(false);
        }
    }

    const onDeleteSchedule = async (id: string) => {
        try {
            await deleteSavedSchedule(id)
        } catch (error) {
            showError("Suppression de la sauvegarde échouée.", error)
            throw error
        }
    }

    const dateRange = useMemo(() => {
        if (!loadedSchedule?.shifts || loadedSchedule.shifts.length === 0) {
            return undefined;
        }

        const startDates = loadedSchedule.shifts.map(s => parseISO(s.start));
        const endDates = loadedSchedule.shifts.map(s => parseISO(s.end));

        if (startDates.length === 0) return undefined;

        let minDate = min(startDates);
        if (startDate) {
            minDate = parseISO(startDate);
        }

        let maxDate = max(endDates);
        if (endDate) {
            maxDate = parseISO(endDate);
        }

        return `${format(minDate, 'yyyy-MM-dd')} à ${format(maxDate, 'yyyy-MM-dd')}`;
    }, [loadedSchedule, startDate, endDate]);

    const sectionParam = useMemo(() => {
        return new URLSearchParams(globalThis.location.search).get("section");
    }, []);

    const combinationRoles: CombinationRole[] = useMemo(() => {
        if (!loadedSchedule) {
            return [];
        }

        if (loadedSchedule.roles && loadedSchedule.roles.length > 0) {
            return loadedSchedule.roles.map(r => ({ id: r.id, name: r.name }));
        }

        const rolesMap = new Map<string, string>();
        for (const shift of loadedSchedule.shifts) {
            if (shift.roleId && !rolesMap.has(shift.roleId)) {
                rolesMap.set(shift.roleId, shift.roleName || shift.requiredSkill || 'Autre');
            }
        }
        for (const emp of loadedSchedule.employees) {
            for (const role of emp.roles ?? []) {
                if (role?.id && !rolesMap.has(role.id)) {
                    rolesMap.set(role.id, role.name);
                }
            }
        }
        return Array.from(rolesMap, ([id, name]) => ({ id, name }));
    }, [loadedSchedule]);

    const unavailableScreen = (variant: "empty" | "error") => (
        <div className="flex items-center justify-center h-screen w-full">
            <ScheduleUnavailableNotice variant={variant} onRetry={handleRefresh} isRetrying={isRefreshing}/>
            <ErrorNotificationContainer errors={errors} onRemove={removeError}/>
        </div>
    );

    if (sectionParam === "combinaisons" || sectionParam === "besoins") {
        if (scheduleLoadState === "empty" || scheduleLoadState === "error") {
            return unavailableScreen(scheduleLoadState);
        }

        if (!loadedSchedule || isRefreshing) {
            return <FullScreenSpinner/>;
        }

        return (
            <div className="px-8 py-4 w-full h-full overflow-y-auto overflow-x-hidden">
                {sectionParam === "combinaisons" && <RoleCombinations roles={combinationRoles}/>}
                {sectionParam === "besoins" &&
                    <RolesSettings schedule={loadedSchedule} startDate={startDate} endDate={endDate}/>}
            </div>
        );
    }

    const solvingProgress = initialUnassignedShiftsCount && unassignedShiftsCount !== null
        ? Math.max(0, Math.min(100, Math.round(((initialUnassignedShiftsCount - unassignedShiftsCount) / initialUnassignedShiftsCount) * 100)))
        : 0;

    const idleCounts = useMemo(() => {
        if (!loadedSchedule) {
            return {
                assigned: 0,
                total: 0
            };
        }

        const period = startDate && endDate ? {
            startDate,
            endDate
        } : null;

        const { assigned, total } = scopedShifts(loadedSchedule.shifts, period);

        return { assigned, total };
    }, [loadedSchedule, startDate, endDate]);

    const globalCounts = useMemo(() => {
        const shifts = loadedSchedule?.shifts ?? [];

        return {
            assigned: shifts.filter((shift) => shift.employee).length,
            total: shifts.length
        };
    }, [loadedSchedule]);

    const idleScore = loadedSchedule?.score ? getScoreComponents(loadedSchedule.score) : null;
    const statusScore = solving ? liveScore : idleScore;

    const liveTimelineKey = `${loadedSchedule?.score ?? "no-score"}`;
    const frozenTimelineKeyRef = useRef<string>(liveTimelineKey);
    if (!solving) frozenTimelineKeyRef.current = liveTimelineKey;
    const timelineKey = solving ? frozenTimelineKeyRef.current : liveTimelineKey;

    const hasAssignedShifts = loadedSchedule?.shifts?.some((s) => s.employee) ?? false;

    return (
        scheduleLoadState === "empty" || scheduleLoadState === "error" ?
            unavailableScreen(scheduleLoadState) :

        !loadedSchedule || isRefreshing ?
            <FullScreenSpinner/> :

            <div className="w-full h-full flex  flex-col">
                {SHOW_MOCK_PLANIFICO_BAR ?
                    <div className="h-12 bg-blue-50 flex  flex-row items-center"
                         style={{
                             flex: "1 0 3.5em",
                             backgroundColor: "#006dc1",
                             color: "white",
                             height: "3.5em",
                             boxShadow: "0 2px 5px rgba(0, 0, 0, 0.26)"
                         }}>
                        <div className="px-2" style={{ width: "4em", paddingLeft: "1em" }}>
                            <ChevronLeft size={24}/>
                        </div>
                        <div className="flex-1">
                            <span className="font-medium relative" style={{ fontSize: 21, top: 0 }}>Génération avancée des horaires</span>
                        </div>
                    </div> : ''
                }
                <div
                    className={`px-8 py-4 w-full flex-1 min-h-0 flex flex-col ${activeTab === 1 || activeTab === 2 ? "overflow-hidden" : "overflow-y-auto overflow-x-hidden"}`}>

                    <div
                        className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${headerCollapsed ? "grid-rows-[0fr]" : "grid-rows-[1fr]"}`}>
                        <div className="overflow-hidden">
                            <Header
                                startDate={startDate}
                                endDate={endDate}
                                dateRange={dateRange}
                                onDatesChange={handleDatesChange}
                                depConfig={depConfigs}
                                onUpdateSolverConfigs={updateUIDepConfigs}
                                onScheduleGenerationSettingsChanged={() => {
                                    if (solvingRef.current) {
                                        return;
                                    }

                                    clearSolveIndicators();
                                    refreshSchedule(null);
                                }}
                                schedule={loadedSchedule}
                                dateFilter={dateFilter}
                                onChangeDateFilter={setDateFilter}
                            />
                        </div>
                    </div>
                    <div className="mb-4">
                        <div className="mb-4">
                            <SolverStatusBar
                                wgLabel={loadedSchedule.wgLabel}
                                assigned={solving ? solveAssigned : idleCounts.assigned}
                                total={solving ? solveTotal : idleCounts.total}
                                globalAssigned={globalCounts.assigned}
                                globalTotal={globalCounts.total}
                                gain={gainDelta}
                                scoreHard={statusScore?.hard ?? null}
                                scoreSoft={statusScore?.soft ?? null}
                            />
                        </div>

                        <SchedulerToolbar
                            solving={solving}
                            startedSolving={startedSolving}
                            tabs={tabs}
                            activeTab={activeTab}
                            onTabChange={setActiveTab}
                            onSolve={solve}
                            onAnalyze={() => analyze()}
                            onApply={apply}
                            onRefresh={handleRefresh}
                            onBenchmark={onSaveSchedule}
                            onImport={onImportSchedule}
                            onDelete={onDeleteSchedule}
                            refreshKey={savedListRefreshKey}
                            onStop={stopSolving}
                            progress={solvingProgress}
                            isApplying={isApplying}
                            isAnalyzing={isAnalyzing}
                            isRefreshing={isRefreshing}
                            isSaving={isSaving}
                            isStopping={isStopping}
                            headerCollapsed={headerCollapsed}
                            onToggleHeaderCollapse={() => setHeaderCollapsed(v => !v)}
                        />
                    </div>

                    <div
                        className={`mt-6 relative ${activeTab === 1 || activeTab === 2 ? "flex-1 min-h-0 overflow-hidden" : ""}`}>
                        {(activeTab === 1 || activeTab === 2) && (
                            <SchedulerTimeline
                                appContext={appContext}
                                key={timelineKey}
                                mode={activeTab === 1 ? "byLocation" : "byEmployee"}
                                solving={solving}
                                schedule={loadedSchedule}
                                startDate={startDate}
                                endDate={endDate}
                                problematicShiftIds={problematicShiftIds}
                                shiftConstraintBreaches={shiftConstraintBreaches}
                                selectedRoles={selectedRoles}
                                selectedEmployees={selectedEmployees}
                                onSelectedRolesChange={setSelectedRoles}
                                onSelectedEmployeesChange={setSelectedEmployees}
                                selectedCalendarOthers={selectedCalendarOthers}
                                onSelectedCalendarOthersChange={setSelectedCalendarOthers}
                                onShiftClick={
                                    (shiftId) => {
                                        // The timeline also reports background items (holidays, time off).
                                        const shift = loadedSchedule.shifts.find(s => s.id === shiftId);
                                        if (!shift) {
                                            setSelectedShiftId(void 0);
                                            return;
                                        }
                                        if (solving) {
                                            setSolvingLockOpen(true);
                                            return;
                                        }
                                        setConfirmOpenShiftActions(true);
                                        setSelectedShiftId(shiftId);
                                    }
                                }
                                onTimelineWindowChange={(window) => {
                                    setAppContext(prev => ({ ...prev, timelineWindow: window }));
                                }}
                            />
                        )}

                        {activeTab === 3 && (
                            <SchedulerEquity schedule={loadedSchedule}/>
                        )}

                        {activeTab === 4 && (
                            <div className="">
                                <ConstraintSetting update={updateConstraints} schedule={loadedSchedule}/>
                            </div>
                        )}

                        {activeTab === 5 && (
                            <RolesSettings schedule={loadedSchedule} startDate={startDate} endDate={endDate}/>
                        )}

                        {activeTab === 6 && (
                            <RoleCombinations roles={combinationRoles}/>
                        )}
                    </div>

                    <ScoreAnalysisModal
                        open={analysisOpen}
                        onClose={() => setAnalysisOpen(false)}
                        score={loadedSchedule.score}
                        analysis={analysisData}
                        fallbackMessage={hasAssignedShifts ? undefined : "Svp assigner les horaires en premier lieu."}
                        definitions={definitions}
                        onShiftClick={focusShiftInTimeline}
                    />

                    <ShiftNotVisibleModal
                        open={shiftNotVisibleReason !== null}
                        onClose={() => setShiftNotVisibleReason(null)}
                        reason={shiftNotVisibleReason}
                    />

                    <SaveModal
                        open={saveModalOpen}
                        onClose={() => setSaveModalOpen(false)}
                        onSave={handleSaveWithName}
                        defaultName={format(new Date(), "yyyy-MM-dd-HH'h'mm'm'ss's'")}
                        isSaving={isSaving}
                    />

                    <SolverPhasesSelectionModal
                        open={confirmOpenPhaseSelect}
                        onClose={() => setConfirmOpenPhaseSelect(false)}
                        onConfirm={async (phaseId, applyDateFilter) => {
                            preserveTimelineScroll();
                            internalSolve(loadedSchedule, phaseId, applyDateFilter)
                            // await updateSolverConfigs(editData);
                            //onUpdateSolverConfigs();
                        }}
                        showCancelButton={true}
                        confirmLabel="Démarrer"
                        title="Sélection de la phase à résoudre"
                        confirmDisabled={false}
                        depConfigs={depConfigs}
                        dateFilter={dateFilter}
                        // onUpdateSolverConfigs={onUpdateSolverConfigs}
                    ></SolverPhasesSelectionModal>
                    <ShiftActionsModal
                        open={confirmOpenShiftActions && !!selectedShiftId}
                        onClose={() => setConfirmOpenShiftActions(false)}
                        onConfirm={async (editData) => {
                            // Solving may have started while this modal was open.
                            if (solving) {
                                setSolvingLockOpen(true);
                                return;
                            }
                            //@ Update shift info in schedule
                            if (loadedSchedule) {
                                let foundShift = loadedSchedule.shifts.find((shift) => {
                                    return shift.id === editData.id
                                });
                                if (foundShift) {
                                    // Replaced rather than mutated: the calendars memoize on the shifts array,
                                    // so an in-place edit would leave them showing the previous assignment.
                                    const updatedShift = {
                                        ...foundShift,
                                        employee: editData.employee || null,
                                        pinned: editData.pinned,
                                    };

                                    const patched = appContext.patchShift?.(updatedShift) ?? false;

                                    if (patched && !solving && appContext.suppressTimelineRebuildRef) {
                                        appContext.suppressTimelineRebuildRef.current = true;
                                    }
                                    setLoadedSchedule({
                                        ...loadedSchedule,
                                        shifts: loadedSchedule.shifts.map(
                                            (shift) => shift.id === updatedShift.id ? updatedShift : shift
                                        ),
                                    });
                                    clearSolveIndicators();
                                }
                            }
                        }}
                        confirmLabel="Confirmer"
                        title="Quart de travail"
                        confirmDisabled={false}
                        shiftId={selectedShiftId}
                        schedule={loadedSchedule}
                        // onUpdateSolverConfigs={onUpdateSolverConfigs}
                    ></ShiftActionsModal>
                    <SolvingLockModal
                        open={solvingLockOpen}
                        onClose={() => setSolvingLockOpen(false)}
                    />
                    <ErrorNotificationContainer errors={errors} onRemove={removeError}/>
                </div>
            </div>
    );
}
